import {
  Button,
  Box,
  Modal as MuiModal,
  Typography,
  Stack,
  SxProps,
  Theme,
  TypographyProps,
  ButtonProps,
} from "@mui/material";
import IconButton, { type IconButtonProps } from "@mui/material/IconButton";
import { ReactNode } from "react";
import { VIOLET } from "@/config/mui/colorPalette";
import { CrossIcon } from "../icons";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "white",
  textAlign: "center",
  boxShadow: "0px 4px 15px rgba(56, 19, 60, 0.25)",
  pt: 6,
  pb: 2,
  px: 2,
  borderRadius: "16px",
  minWidth: { xs: "95%", sm: "550px" },
  maxWidth: "95%",
  minHeight: "400px",
} as const;

export default function Modal({
  open,
  handleClose,
  confirm,
  confirmDisabled,
  discard,
  title,
  description,
  discardButtonText = "Cancel",
  confirmButtonText = "Confirm",
  children,
  CloseIconButtonProps,
  DescriptionProps,
  TitleProps,
  closeIcon,
  ButtonProps,
  sx,
  childrenWrapperSx,
}: {
  open: boolean;
  handleClose: () => void;
  confirm?: () => void;
  confirmDisabled?: boolean;
  discard?: () => void;
  title?: string;
  description?: string;
  discardButtonText?: string;
  confirmButtonText?: string;
  children: ReactNode;
  CloseIconButtonProps?: IconButtonProps;
  DescriptionProps?: Partial<TypographyProps>;
  TitleProps?: Partial<TypographyProps>;
  closeIcon?: React.ReactNode;
  ButtonProps?: Omit<Partial<ButtonProps>, "onClick">;
  sx?: SxProps<Theme>;
  childrenWrapperSx?: SxProps<Theme>;
}) {
  return (
    <MuiModal open={open} onClose={handleClose} sx={{ overflow: "scroll" }}>
      <Box sx={{ ...style, ...sx }}>
        <IconButton
          onClick={handleClose}
          color="primary"
          {...CloseIconButtonProps}
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            cursor: "pointer",
            ...CloseIconButtonProps?.sx,
          }}
        >
          {closeIcon ?? <CrossIcon color={VIOLET[90]} />}
        </IconButton>
        {title && (
          <Typography variant="h3" {...TitleProps}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            {...DescriptionProps}
            sx={{
              color: "secondary.main",
              mt: 2,
              mb: 4,
              ...DescriptionProps?.sx,
            }}
          >
            {description}
          </Typography>
        )}
        <Box sx={childrenWrapperSx}>{children}</Box>
        <Stack
          spacing={1}
          direction="row"
          sx={{
            justifyContent: "center",
            width: "100%",
          }}
        >
          {discard && (
            <Button
              variant="outlined"
              onClick={discard}
              {...ButtonProps}
              sx={{
                width: "140px",
                borderRadius: "16px",
                py: 2,
                height: "56px",
                ...ButtonProps?.sx,
              }}
            >
              {discardButtonText}
            </Button>
          )}
          {confirm && (
            <Button
              variant="contained"
              onClick={confirm}
              disabled={confirmDisabled}
              {...ButtonProps}
              sx={{
                width: "140px",
                borderRadius: "16px",
                py: 2,
                height: "56px",
                ...ButtonProps?.sx,
              }}
            >
              {confirmButtonText}
            </Button>
          )}
        </Stack>
      </Box>
    </MuiModal>
  );
}
